import React, { useState, useRef } from 'react';
import {
  Box, Typography, IconButton, CircularProgress,
  TextField, Button, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, makeStyles
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import ImageIcon from '@material-ui/icons/Image';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import SettingsIcon from '@material-ui/icons/Settings';
import {
  uploadToImgBB,
  uploadToFirebaseStorage,
  getImgBBApiKey,
  setImgBBApiKey
} from '../services/imageUploadService';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  headerBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
    flexWrap: 'wrap',
    gap: 8,
  },
  modeButtonGroup: {
    display: 'flex',
    gap: 6,
    alignItems: 'center',
  },
  modeChip: {
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
    borderRadius: 8,
  },
  dropZone: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: 14,
    padding: theme.spacing(3, 2),
    textAlign: 'center',
    cursor: 'pointer',
    background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: '#10b981',
      background: 'rgba(16,185,129,0.04)',
    },
  },
  previewContainer: {
    position: 'relative',
    borderRadius: 14,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? '#0f172a' : '#f8fafc',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  previewImg: {
    width: '100%',
    maxHeight: 220,
    objectFit: 'contain',
    background: theme.palette.type === 'dark' ? '#020617' : '#f1f5f9',
    display: 'block',
  },
  previewOverlay: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    background: theme.palette.type === 'dark' ? 'rgba(15,23,42,0.95)' : 'rgba(255,255,255,0.95)',
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  deleteBtn: {
    color: '#ef4444',
    padding: 6,
    '&:hover': {
      background: 'rgba(239,68,68,0.1)',
    },
  },
  externalLinks: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: 6,
  },
  externalBtn: {
    fontSize: '0.72rem',
    textTransform: 'none',
    color: theme.palette.text.secondary,
    padding: '2px 8px',
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      borderColor: '#10b981',
      color: '#10b981',
    },
  },
}));

export default function ImageUploadBox({
  value,
  initialUrl,
  onChange,
  onUploadComplete,
  folder = 'uploads',
  storagePath,
  label = 'Upload Image',
  helperText = 'Direct upload or paste image URL',
  aspectRatio,
  defaultMode,
}) {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Computed values
  const currentValue = value !== undefined ? value : (initialUrl || '');
  const currentFolder = storagePath || folder || 'uploads';

  const triggerChange = (newUrl) => {
    if (typeof onChange === 'function') {
      onChange(newUrl);
    }
    if (typeof onUploadComplete === 'function') {
      onUploadComplete(newUrl);
    }
  };

  // Provider mode: 'storage' (firebase), 'imgbb' (free host), or 'url' (direct url)
  const [mode, setMode] = useState(() => {
    if (defaultMode) return defaultMode;
    if (storagePath) return 'storage';
    if (getImgBBApiKey()) return 'imgbb';
    return 'storage';
  });

  // ImgBB Key Dialog
  const [keyDialogOpen, setKeyDialogOpen] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState(getImgBBApiKey());
  const [statusMessage, setStatusMessage] = useState('');

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (PNG, JPG, WebP, GIF)');
      return;
    }

    // If ImgBB mode but no key configured:
    if (mode === 'imgbb') {
      const currentKey = getImgBBApiKey();
      if (!currentKey) {
        setKeyDialogOpen(true);
        setStatusMessage('Please enter your free ImgBB API key to upload directly, or switch to "Paste URL" mode.');
        return;
      }
    }

    setUploading(true);
    setStatusMessage('');

    try {
      if (mode === 'imgbb') {
        const url = await uploadToImgBB(file);
        triggerChange(url);
      } else {
        // Firebase Storage
        const url = await uploadToFirebaseStorage(file, currentFolder);
        triggerChange(url);
      }
    } catch (err) {
      console.error('Upload failed:', err);
      if (err.message === 'MISSING_IMGBB_KEY') {
        setKeyDialogOpen(true);
      } else if (err.code === 'storage/unauthorized' || (err.message && err.message.includes('404'))) {
        alert(
          'Firebase Storage is not enabled or requires Blaze plan (404 Not Found).\n' +
          'Switched to Free ImgBB / Direct URL upload.'
        );
        setMode('imgbb');
      } else {
        alert(`Upload error: ${err.message || 'Check storage permissions or ImgBB key'}`);
      }
    }

    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleRemove = () => {
    triggerChange('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSaveApiKey = () => {
    setImgBBApiKey(apiKeyInput);
    setKeyDialogOpen(false);
    alert('ImgBB API key saved! You can now upload images directly.');
  };

  return (
    <Box className={classes.container}>
      <input
        type="file"
        ref={fileInputRef}
        hidden
        accept="image/*"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {/* Mode Selector Header */}
      <Box className={classes.headerBar}>
        <Typography variant="caption" style={{ fontWeight: 600, color: '#10b981' }}>
          {label}
        </Typography>
        <Box className={classes.modeButtonGroup}>
          <Chip
            size="small"
            label="ImgBB (Free Host)"
            color={mode === 'imgbb' ? 'primary' : 'default'}
            variant={mode === 'imgbb' ? 'default' : 'outlined'}
            className={classes.modeChip}
            onClick={() => setMode('imgbb')}
          />
          <Chip
            size="small"
            label="Paste URL"
            color={mode === 'url' ? 'primary' : 'default'}
            variant={mode === 'url' ? 'default' : 'outlined'}
            className={classes.modeChip}
            onClick={() => setMode('url')}
          />
          <Chip
            size="small"
            label="Firebase"
            color={mode === 'storage' ? 'primary' : 'default'}
            variant={mode === 'storage' ? 'default' : 'outlined'}
            className={classes.modeChip}
            onClick={() => setMode('storage')}
          />
          {mode === 'imgbb' && (
            <IconButton
              size="small"
              onClick={() => {
                setApiKeyInput(getImgBBApiKey());
                setKeyDialogOpen(true);
              }}
              title="Configure ImgBB API Key"
              style={{ padding: 4 }}
            >
              <SettingsIcon fontSize="small" style={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Preview or Upload Zone */}
      {currentValue ? (
        <Box className={classes.previewContainer}>
          <img
            src={currentValue}
            alt="Uploaded Preview"
            className={classes.previewImg}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <Box className={classes.previewOverlay}>
            <Box display="flex" alignItems="center" style={{ gap: 8, overflow: 'hidden' }}>
              <ImageIcon fontSize="small" style={{ color: '#10b981' }} />
              <Typography variant="caption" noWrap style={{ maxWidth: 360, fontWeight: 500 }}>
                {currentValue}
              </Typography>
            </Box>
            <IconButton size="small" className={classes.deleteBtn} onClick={handleRemove} title="Remove image">
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      ) : mode === 'url' ? (
        <Box>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="https://i.ibb.co/... or https://i.postimg.cc/... or any direct image URL"
            value={currentValue || ''}
            onChange={(e) => triggerChange(e.target.value)}
            helperText="Paste any public direct image URL. It will be sent to mobile app & notifications."
          />
          <Box className={classes.externalLinks}>
            <Typography variant="caption" color="textSecondary">
              Quick upload &amp; get direct link:
            </Typography>
            <Button
              size="small"
              className={classes.externalBtn}
              startIcon={<OpenInNewIcon fontSize="inherit" />}
              href="https://imgbb.com/upload"
              target="_blank"
              rel="noopener noreferrer"
            >
              ImgBB.com (Free)
            </Button>
            <Button
              size="small"
              className={classes.externalBtn}
              startIcon={<OpenInNewIcon fontSize="inherit" />}
              href="https://postimages.org/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Postimages.org (No account)
            </Button>
            <Button
              size="small"
              className={classes.externalBtn}
              startIcon={<OpenInNewIcon fontSize="inherit" />}
              href="https://unsplash.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Unsplash
            </Button>
          </Box>
        </Box>
      ) : (
        <Box
          className={classes.dropZone}
          style={{
            borderColor: dragOver ? '#10b981' : undefined,
            background: dragOver ? 'rgba(16,185,129,0.08)' : undefined,
          }}
          onClick={() => !uploading && fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          {uploading ? (
            <Box py={2} display="flex" flexDirection="column" alignItems="center" style={{ gap: 8 }}>
              <CircularProgress size={32} style={{ color: '#10b981' }} />
              <Typography variant="body2" color="textSecondary">
                Uploading via {mode === 'imgbb' ? 'ImgBB (Free CDN)' : 'Firebase Storage'}...
              </Typography>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" alignItems="center" style={{ gap: 4 }}>
              <CloudUploadIcon style={{ fontSize: 36, color: '#10b981', marginBottom: 4 }} />
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                {mode === 'imgbb' ? 'Direct Free Upload via ImgBB' : 'Upload to Firebase Storage'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Click to browse or drag and drop image here
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ opacity: 0.8 }}>
                {mode === 'imgbb'
                  ? 'Free permanent CDN hosting (PNG, JPG, WebP up to 32MB)'
                  : helperText}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* ImgBB API Key Setup Dialog */}
      <Dialog
        open={keyDialogOpen}
        onClose={() => setKeyDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <VpnKeyIcon style={{ color: '#10b981' }} />
            <Typography variant="h6">Free ImgBB API Key</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" paragraph>
            Since Firebase Storage requires the Blaze billing plan, you can use <strong>ImgBB</strong> to upload images for free forever without any credit card!
          </Typography>
          <Typography variant="caption" display="block" style={{ marginBottom: 12 }}>
            <strong>How to get your free key in 10 seconds:</strong><br />
            1. Open{' '}
            <a
              href="https://api.imgbb.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#10b981', fontWeight: 600 }}
            >
              api.imgbb.com <OpenInNewIcon style={{ fontSize: 12, verticalAlign: 'middle' }} />
            </a><br />
            2. Log in with Google &amp; click <strong>"Get API key"</strong>.<br />
            3. Copy the key and paste it below:
          </Typography>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label="ImgBB API Key"
            placeholder="e.g. 5d8e7..."
            value={apiKeyInput}
            onChange={(e) => setApiKeyInput(e.target.value)}
          />
          {statusMessage && (
            <Typography variant="caption" color="error" style={{ marginTop: 8, display: 'block' }}>
              {statusMessage}
            </Typography>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '8px 24px 16px' }}>
          <Button
            onClick={() => {
              setKeyDialogOpen(false);
              setMode('url');
            }}
            color="default"
          >
            Use Manual URL Instead
          </Button>
          <Button
            onClick={handleSaveApiKey}
            variant="contained"
            style={{ background: '#10b981', color: '#fff' }}
            disabled={!apiKeyInput.trim()}
          >
            Save Key
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
